const isValidImage = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

const getImageUrl = (item: any): string | null => {
  if (!item) return null;

  if (typeof item === 'string') return item;

  if (typeof item === 'object' && typeof item.url === 'string') {
    return item.url;
  }

  return null;
};


export const useValidateThumbnailBuilder = async (data: { thumbnail?: string; gallery?: string[]; image?: string[] }): Promise<string> => {
  if (!data) {
    throw new Error("Invalid data passed to useValidateThumbnailBuilder");
  }
  let imageURL: string | undefined = undefined;
  const { thumbnail, gallery = [], image = [] } = data;  // Set default empty arrays
  const runtimeConfig = useRuntimeConfig();
  const toSecureUrl = (url: string): string => url.replace(/^http:\/\//, 'https://');

  // Validate thumbnail first
  if (thumbnail && thumbnail !== '') {
    const secureThumbnail = toSecureUrl(thumbnail);
    const valid = await isValidImage(secureThumbnail);
    if (valid) {
      return secureThumbnail;  // Early return if thumbnail is valid
    }
  }

  // Validate gallery images in parallel
  if (gallery.length > 0) {
    const validImagePromises = gallery.map((url) => {
      const secureUrl = toSecureUrl(url);
      return isValidImage(secureUrl).then(valid => (valid ? secureUrl : null));
    });
    const validImages = await Promise.all(validImagePromises);
    imageURL = validImages.find(url => url !== null) as string | undefined;
  }

  // Validate image array if gallery is empty
  if (Array.isArray(image) && image.length > 0) {
    const validImagePromises = image.map(async (item) => {
      let rawUrl: string | null = null;

      if (typeof item === 'string' && item.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(item);
          rawUrl = parsed?.url ?? null;
        } catch {
          rawUrl = null;
        }
      }
      
      else if (typeof item === 'string') {
        rawUrl = item;
      }

      if (!rawUrl) return null;

      const imageUrl = getImageUrl(rawUrl);
      if (!imageUrl) return null;

      const secureUrl = toSecureUrl(imageUrl);
      const isValid = await isValidImage(secureUrl);

      return isValid ? secureUrl : null;
    });

    const validImages = await Promise.all(validImagePromises);
    imageURL = validImages.find((url): url is string => Boolean(url));
  }


  // Fallback to default image
  if (!imageURL) {
    imageURL = runtimeConfig.public.defaultImage;
  }

  return imageURL;
};

